import {
  Box,
  Grid,
  Text,
  SimpleGrid,
  useColorModeValue,
  IconButton,
  useDisclosure,
  Input,
  Button,
  Textarea,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Badge,
  Select,
} from '@chakra-ui/react';
import {
  AttachmentIcon,
  CheckIcon,
  ChevronDownIcon,
  PhoneIcon,
  SettingsIcon,
} from '@chakra-ui/icons';
import React, { useCallback, useEffect, useState } from 'react';
import Card from 'components/card/Card';
import BackButton from 'components/BackButton';
import Loader from 'loaders/Loader';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { createAPIEndPoint } from 'config/api/api';
import { logoutUser } from 'utils';
import { MdClose, MdEdit } from 'react-icons/md';
import AIAgentModal from '../components/AIAgentModal';
import { getUserData } from 'utils';

const serviceColors = {
  openai: 'teal',
  deepgram: 'red',
};

export default function AIAgentDetails() {
  const navigate = useNavigate();
  const location = useLocation();

  const [allTools, setAllTools] = useState([]);
  const [selectedTools, setSelectedTools] = useState([]);
  const [toolSearch, setToolSearch] = useState('');
  const [isSavingTools, setIsSavingTools] = useState(false);

  const userData = getUserData();
  const clinic_id = userData?.clinic_id ?? null;

  const id = location.pathname.split('/').pop();

  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const bgColor = useColorModeValue('gray.50', 'whiteAlpha.100');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const textColorSecondary = 'gray.400';

  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [phoneSearch, setPhoneSearch] = useState('');
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [isSavingPhones, setIsSavingPhones] = useState(false);
  const [assistant, setAssistant] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    model: '',
    assistant_type: '',
    messages: '',
    temperature: '',
    instructions: '',
  });
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchTools = async () => {
    try {
      const res = await createAPIEndPoint('tools').fetchAll();
      setAllTools(res.data || []);
    } catch (err) {
      toast.error('Failed to fetch tools');
    }
  };

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchPhoneNumbers = useCallback(
    async (stopLoading = false) => {
      try {
        const response = await createAPIEndPoint(
          `locations/phones/all/${clinic_id}`,
        ).fetchAll();

        let phones = response.data.phones || [];
        phones = phones.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.id === item.id),
        );

        setPhoneNumbers(phones);
      } catch (err) {
        console.error('❌ Error fetching locations phone number:', err);
        toast.error('Failed to load phone numbers'); // <-- ✅ show toast
      }
    },
    [clinic_id],
  );

  useEffect(() => {
    if (clinic_id) {
      fetchPhoneNumbers();
    }
  }, [clinic_id]);

  const filteredPhoneNumbers = phoneNumbers.filter(
    (p) =>
      p.phone_number.toLowerCase().includes(phoneSearch.toLowerCase()) ||
      p.location_name.toLowerCase().includes(phoneSearch.toLowerCase()),
  );

  const fetchAssistant = async () => {
    try {
      setLoading(true);
      const response = await createAPIEndPoint(`assistants/`).fetchById(id);
      const data = response.data;
      setAssistant(data);
      setFormData({
        name: data?.name || '',
        model: data?.model || '',
        temperature: parseFloat(data?.temperature) || '',
        instructions: data?.instructions || '',
        assistant_type: data?.type || '',
        messages: data?.messages || '',
      });

      // 💡 Pre-fill selectedNumbers using phone_number_ids → phone_number
      if (Array.isArray(data.assigned_phone_number_ids)) {
        const selectedPhones = phoneNumbers.filter((p) =>
          data.assigned_phone_number_ids.includes(p.id),
        );

        if (Array.isArray(data.tool_ids)) {
          setSelectedTools(data.tool_ids);
        }

        const selectedNums = selectedPhones.map((p) => p.phone_number);
        setSelectedNumbers(selectedNums);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error || 'Error fetching assistant details',
      );
      if (error?.response?.data?.error?.includes('Bearer token')) {
        logoutUser(navigate);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (assistant?.assigned_phone_number_ids && phoneNumbers.length > 0) {
      const selectedPhones = phoneNumbers.filter((p) =>
        assistant.assigned_phone_number_ids.includes(p.id),
      );
      const selectedNums = selectedPhones.map((p) => p.phone_number);
      setSelectedNumbers(selectedNums);
    }
  }, [assistant, phoneNumbers]);

  useEffect(() => {
    if (id) {
      fetchAssistant();
    }
  }, [id]);

  const handleEditClick = () => {
    setIsEditing(!isEditing);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const handleSave = async () => {
  //   try {
  //     await createAPIEndPoint(`assistants/`).update(id, formData);
  //     toast.success('Assistant updated successfully');
  //     setIsEditing(false);
  //     fetchAssistant();
  //   } catch (error) {
  //     toast.error(error?.response?.data?.error || 'Update failed');
  //   }
  // };

  // const handleSave = async () => {
  //   try {
  //     const payload = {
  //       ...formData,
  //       temperature: parseFloat(formData.temperature),
  //     };

  //     await createAPIEndPoint(`assistants/`).update(id, payload);
  //     toast.success('Assistant updated successfully');
  //     setIsEditing(false);
  //     fetchAssistant();
  //   } catch (error) {
  //     toast.error(error?.response?.data?.error || 'Update failed');
  //   }
  // };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        temperature: parseFloat(formData.temperature),
      };

      await createAPIEndPoint(`assistants/`).update(id, payload);
      toast.success('Assistant updated successfully');
      setIsEditing(false);
      fetchAssistant();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Update failed');
    }
  };

  const handleAddNumber = (number) => {
    if (!selectedNumbers.includes(number)) {
      setSelectedNumbers([...selectedNumbers, number]);
    }
  };

  const handleRemoveNumber = (number) => {
    setSelectedNumbers((prev) => prev.filter((n) => n !== number));
  };

  const handleSaveNumbers = async () => {
    setIsSavingPhones(true);

    try {
      const phoneIds = selectedNumbers
        .map((number) => {
          const phone = phoneNumbers.find((p) => p.phone_number === number);
          return phone?.id;
        })
        .filter(Boolean); // remove undefined/null

      const payload = { phone_number_ids: phoneIds };

      await createAPIEndPoint(`assistants/`).update(id, payload);

      toast.success('Phone numbers assigned successfully');
    } catch (error) {
      console.error('❌ Failed to assign phone numbers:', error);
      toast.error('Failed to assign phone numbers');
    } finally {
      setIsSavingPhones(false);
    }
  };

  // const handleEditClick = () => {
  //   onOpen();
  // };

  return (
    <>
      {loading && <Loader />}
      <Box
        pt={{ base: '130px', md: '80px', xl: '80px' }}
        height="100%"
        overflow="hidden"
      >
        <BackButton />

        <Grid templateColumns={{ base: '1fr' }} gap={{ base: '20px' }}>
          <Card>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb="24px"
            >
              <Text color={textColorPrimary} fontWeight="bold" fontSize="2xl">
                Assistant Details
              </Text>
              <IconButton
                icon={
                  isEditing ? (
                    <MdClose />
                  ) : (
                    <MdEdit style={{ marginLeft: '-1px' }} />
                  )
                }
                aria-label={isEditing ? 'Close Editor' : 'Edit Assistant'}
                colorScheme={isEditing ? 'red' : 'brand'}
                size="sm"
                onClick={handleEditClick}
              />
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Name:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {isEditing ? (
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      mt={1}
                      style={{
                        fontSize: 14,
                        height: '2.15rem',
                        borderRadius: '2px',
                      }}
                    />
                  ) : (
                    <Text color={textColorSecondary} fontSize="md">
                      {assistant?.name || 'N/A'}
                    </Text>
                  )}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Model:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {isEditing ? (
                    <Input
                      name="model"
                      value={formData.model}
                      onChange={handleChange}
                      mt={1}
                      style={{
                        fontSize: 14,
                        height: '2.15rem',
                        borderRadius: '2px',
                      }}
                    />
                  ) : (
                    <Text color={textColorSecondary} fontSize="md">
                      {assistant?.model || 'N/A'}
                    </Text>
                  )}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Type:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {isEditing ? (
                    <Select
                      name="assistant_type"
                      value={formData.assistant_type || ''}
                      onChange={handleChange}
                      fontSize="sm"
                      placeholder="Select type"
                    >
                      <option value="email">Email</option>
                      <option value="chat">Chat</option>
                      <option value="sms">SMS</option>
                      <option value="voice">Voice</option>
                      <option value="text">Text</option>
                    </Select>
                  ) : (
                    <Text
                      color={textColorSecondary}
                      textTransform="capitalize"
                      fontSize="md"
                    >
                      {assistant?.type || 'N/A'}
                    </Text>
                  )}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Temperature:
                </Text>
                {isEditing ? (
                  <Input
                    name="temperature"
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    mt={1}
                    value={formData.temperature}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        temperature: parseFloat(e.target.value),
                      }))
                    }
                    style={{
                      fontSize: 14,
                      height: '2.15rem',
                      borderRadius: '2px',
                    }}
                  />
                ) : (
                  <Text color={textColorSecondary} fontSize="md">
                    {assistant?.temperature ?? 'N/A'}
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Assistant ID:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {assistant?.assistant_id || 'N/A'}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Service:
                </Text>
                <Badge
                  colorScheme={serviceColors[assistant?.service] || 'gray'}
                  px={2}
                  py={1.5}
                  borderRadius="md"
                  textAlign="center"
                  minWidth="60px"
                >
                  <Text
                    fontSize="sm"
                    textTransform="capitalize"
                    fontWeight="700"
                  >
                    {assistant?.service}
                  </Text>
                </Badge>
              </Box>

              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Clinic Name:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {assistant?.clinic_name || 'N/A'}
                </Text>
              </Box>

              <Box gridColumn="1 / -1">
                <Text fontWeight="semibold" color={textColorPrimary} mb={2}>
                  Instructions:
                </Text>
                {isEditing ? (
                  <Textarea
                    name="instructions"
                    value={formData.instructions}
                    onChange={handleChange}
                    rows={6}
                    style={{
                      // backgroundColor: '#F7FAFC',
                      fontSize: 14,
                      // color: '#a0aec0',
                      // maxHeight: '400px',
                      minHeight: '400px',
                      borderRadius: '2px',
                    }}
                  />
                ) : (
                  <Box
                    bg={bgColor}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="12px"
                    p={4}
                    maxHeight="400px"
                    overflowY="auto"
                    whiteSpace="pre-wrap"
                    fontSize="sm"
                    color={textColorSecondary}
                    lineHeight="1.6"
                  >
                    {(() => {
                      try {
                        const raw = assistant?.instructions || '';
                        return typeof raw === 'string'
                          ? JSON.parse(`"${raw}"`)
                          : raw;
                      } catch (err) {
                        return assistant?.instructions || 'N/A';
                      }
                    })()}
                  </Box>
                )}
              </Box>
              {assistant?.messages && (
                <Box gridColumn="1 / -1">
                  <Text fontWeight="semibold" color={textColorPrimary} mb={2}>
                    Prompt:
                  </Text>
                  {isEditing ? (
                    <Textarea
                      name="messages"
                      value={formData.messages}
                      onChange={handleChange}
                      rows={6}
                      style={{
                        // backgroundColor: '#F7FAFC',
                        fontSize: 14,
                        // color: '#a0aec0',
                        // maxHeight: '400px',
                        minHeight: '200px',
                        borderRadius: '2px',
                      }}
                    />
                  ) : (
                    <Box
                      bg={bgColor}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="12px"
                      p={4}
                      maxHeight="200px"
                      overflowY="auto"
                      whiteSpace="pre-wrap"
                      fontSize="sm"
                      color={textColorSecondary}
                      lineHeight="1.6"
                    >
                      {(() => {
                        try {
                          const raw = assistant?.messages || '';
                          return typeof raw === 'string'
                            ? JSON.parse(`"${raw}"`)
                            : raw;
                        } catch (err) {
                          return assistant?.messages || 'N/A';
                        }
                      })()}
                    </Box>
                  )}
                </Box>
              )}
            </SimpleGrid>
            {isEditing && (
              <Box textAlign="right" mt={4}>
                <Button colorScheme="brand" onClick={handleSave}>
                  Save
                </Button>
              </Box>
            )}
          </Card>
        </Grid>

        <AIAgentModal
          isOpen={isOpen}
          onClose={onClose}
          type="edit"
          assistantId={id}
          fetchAgain={fetchAssistant}
        />
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Card mt={8}>
            <Box>
              <Text
                fontWeight="bold"
                fontSize="xl"
                mb={4}
                display="flex"
                alignItems="center"
                gap={2}
              >
                <SettingsIcon boxSize={5} color="brand.500" />
                Assign Tools
              </Text>

              <Menu>
                <Box
                  w={{ base: '100%', sm: '280px' }}
                  mb={4}
                  position="relative"
                >
                  <MenuButton
                    as={Button}
                    variant="unstyled"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="16px"
                    px={4}
                    pr="36px"
                    fontSize="sm"
                    fontWeight="500"
                    textAlign="left"
                    w="100%"
                  >
                    Select Tool
                  </MenuButton>
                  <ChevronDownIcon
                    position="absolute"
                    top="50%"
                    right="12px"
                    transform="translateY(-50%)"
                    pointerEvents="none"
                    color="gray.500"
                  />
                </Box>

                <MenuList
                  maxH="300px"
                  overflowY="auto"
                  borderRadius="12px"
                  border="1px solid"
                  borderColor="gray.200"
                  shadow="lg"
                  mt={2}
                  minW="280px" // ✅ ADD this line
                  py={2}
                >
                  <Box
                    px={3}
                    pb={2}
                    position="sticky"
                    top="0"
                    zIndex="1"
                    bg="white"
                  >
                    <Input
                      placeholder="Search tools..."
                      size="md"
                      fontSize="sm"
                      borderRadius="8px"
                      value={toolSearch}
                      onChange={(e) => setToolSearch(e.target.value)}
                    />
                  </Box>

                  {allTools
                    .filter((tool) =>
                      tool.name
                        .toLowerCase()
                        .includes(toolSearch.toLowerCase()),
                    )
                    .map((tool) => {
                      const isSelected = selectedTools.includes(tool.id);
                      return (
                        <MenuItem
                          key={tool.id}
                          fontSize="sm"
                          fontWeight="500"
                          pl={8}
                          pr={3}
                          py={2}
                          onClick={() => {
                            if (!isSelected) {
                              setSelectedTools([...selectedTools, tool.id]);
                            }
                          }}
                          position="relative"
                          _hover={{ bg: isSelected ? 'gray.200' : 'gray.100' }}
                        >
                          {isSelected && (
                            <CheckIcon
                              boxSize={3.5}
                              color="brand.500"
                              position="absolute"
                              left="10px"
                            />
                          )}
                          {tool.name}
                        </MenuItem>
                      );
                    })}
                </MenuList>
              </Menu>

              {selectedTools.length > 0 && (
                <Box mt={2} display="flex" flexWrap="wrap" gap="8px">
                  {selectedTools.map((id) => {
                    const tool = allTools.find((t) => t.id === id);
                    return (
                      <Tag
                        key={id}
                        borderRadius="12px"
                        bg="white"
                        color="gray.800"
                        px={3}
                        py={1.5}
                        fontSize="13px"
                        fontWeight="500"
                        border="1px solid"
                        borderColor="gray.300"
                        width="fit-content"
                      >
                        <TagLabel>{tool?.name || 'Unknown'}</TagLabel>
                        <TagCloseButton
                          onClick={() =>
                            setSelectedTools((prev) =>
                              prev.filter((tid) => tid !== id),
                            )
                          }
                          color="red"
                        />
                      </Tag>
                    );
                  })}
                </Box>
              )}

              <Box textAlign="right" mt={6}>
                <Button
                  colorScheme="brand"
                  onClick={async () => {
                    setIsSavingTools(true);
                    try {
                      await createAPIEndPoint(`assistants/`).update(id, {
                        tool_ids: selectedTools,
                      });
                      toast.success('Tools assigned successfully');
                    } catch (error) {
                      toast.error('Failed to assign tools');
                    } finally {
                      setIsSavingTools(false);
                    }
                  }}
                  isLoading={isSavingTools}
                  loadingText="Saving..."
                >
                  Save Tools
                </Button>
              </Box>
            </Box>
          </Card>

          <Card mt={8}>
            <Box>
              <Text
                color={textColorPrimary}
                fontWeight="bold"
                fontSize="xl"
                mb={4}
                display="flex"
                alignItems="center"
                gap={2}
              >
                <AttachmentIcon boxSize={5} color="brand.500" />
                Assign Phone Numbers
              </Text>

              {/* Menu for phone number selection */}
              <Menu>
                <Box
                  position="relative"
                  display="inline-block"
                  w={{ base: '100%', sm: '280px' }}
                  mb={4}
                >
                  <MenuButton
                    as={Button}
                    variant="unstyled"
                    fontSize="sm"
                    fontWeight="500"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="16px"
                    px="14px"
                    pr="36px"
                    _focus={{
                      borderColor: 'brand.500',
                    }}
                    width="100%"
                    textAlign="left"
                  >
                    Select Phone Number
                  </MenuButton>
                  <ChevronDownIcon
                    position="absolute"
                    top="50%"
                    right="12px"
                    transform="translateY(-50%)"
                    pointerEvents="none"
                    color="gray.500"
                  />
                </Box>

                <MenuList
                  borderRadius="12px"
                  border="1px solid"
                  borderColor="gray.200"
                  shadow="lg"
                  mt={2}
                  minW="280px"
                  py={2}
                  maxH="300px"
                  overflowY="auto"
                >
                  <Box
                    px={3}
                    pb={2}
                    position="sticky"
                    top="0"
                    zIndex="1"
                    bg="white"
                  >
                    <Input
                      placeholder="Search phone number..."
                      size="md"
                      fontSize="sm"
                      borderRadius="8px"
                      onChange={(e) => setPhoneSearch(e.target.value)}
                      value={phoneSearch}
                    />
                  </Box>

                  <Box minH="200px" maxH="200px" overflowY="auto">
                    {filteredPhoneNumbers.length > 0 ? (
                      filteredPhoneNumbers.map((phone) => {
                        const number = phone.phone_number;
                        const location = phone.location_name;
                        const isSelected = selectedNumbers.includes(number);

                        return (
                          <MenuItem
                            key={`${phone.id}-${number}`}
                            fontSize="sm"
                            fontWeight="500"
                            bg="transparent"
                            _hover={{
                              bg: isSelected ? 'gray.200' : 'gray.100',
                            }}
                            pl={8}
                            pr={3}
                            py={2}
                            onClick={() => handleAddNumber(number)}
                            display="flex"
                            alignItems="center"
                            position="relative"
                            gap={2}
                          >
                            {isSelected && (
                              <CheckIcon
                                sx={{ position: 'absolute', left: '10px' }}
                                boxSize={3.5}
                                color="brand.500"
                              />
                            )}
                            <Box>
                              <Text>{number}</Text>
                              <Text fontSize="xs" color="gray.500">
                                {location}
                              </Text>
                            </Box>
                          </MenuItem>
                        );
                      })
                    ) : (
                      <Box px={4} py={2}>
                        <Text fontSize="sm" color="gray.500">
                          No phone numbers found.
                        </Text>
                      </Box>
                    )}
                  </Box>
                </MenuList>
              </Menu>

              {/* Tags Section in Columns */}
              {selectedNumbers.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedNumbers.map((number) => {
                    const phone = phoneNumbers.find(
                      (p) => p.phone_number === number,
                    );
                    const location = phone?.location_name || 'Unknown';

                    return (
                      <Tag
                        key={number}
                        borderRadius="12px"
                        bg="white"
                        color="gray.800"
                        px={3}
                        py={1.5}
                        fontSize="13px"
                        fontWeight="500"
                        border="1px solid"
                        borderColor="gray.300"
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        width="fit-content"
                      >
                        <TagLabel>
                          {number} ({location})
                        </TagLabel>
                        <TagCloseButton
                          onClick={() => handleRemoveNumber(number)}
                          color="red"
                        />
                      </Tag>
                    );
                  })}
                </div>
              )}

              <Box textAlign="right" mt={6}>
                <Button
                  colorScheme="brand"
                  onClick={handleSaveNumbers}
                  isLoading={isSavingPhones}
                  loadingText="Saving..."
                >
                  Save Phone Numbers
                </Button>
              </Box>
            </Box>
          </Card>
        </SimpleGrid>
      </Box>
    </>
  );
}
